import * as React from 'react'

import { makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import WorkflowDirectory from '~/client/src/shared/components/WorkflowDirectory/WorkflowDirectory'
import {
  isWorkflowTypeSelectable,
  resolveAllowedWorkflowTypes,
} from '~/client/src/shared/constants/FilterPropertyMaps/WorkflowFilterPropMap'
import { isFieldMultiple } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { fromDefaultOptions } from '~/client/src/shared/utils/linkedWorkflowDefaults.utils'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import SitePermitCreationFormStore from '../../../../../WorkflowCreationForm.store'

interface IProps {
  store: SitePermitCreationFormStore
  isPreviewMode: boolean
  onClose?: () => void
  workflowTypesStore?: WorkflowTypesStore
}

@inject('workflowTypesStore')
@observer
export default class FormWorkflowPicker extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public render() {
    const allowedTypeIds = this.allowedWorkflowTypes.map(type => type.id)

    const defaultOptions = this.localProps.store.selectedField?.defaultOptions
    const defaults = fromDefaultOptions(
      defaultOptions,
      this.localProps.workflowTypesStore,
    )

    return (
      <WorkflowDirectory
        workflowPredicate={this.existingWorkflowsFilter}
        selectedWorkflowIds={this.localProps.store.selectedFieldValues}
        filters={{
          searchString: this.localProps.store.debouncedModalSearchValue,
        }}
        onSave={this.onSave}
        onCancel={this.localProps.onClose}
        showQuickFilters
        allowCreateNew={this.allowCreateNew}
        allowSelectExisting={this.allowSelectExisting}
        initialCreating={
          this.localProps.store.fieldSelectionMode === 'createNew'
        }
        allowedTypeIds={allowedTypeIds}
        isMultiple={isFieldMultiple(
          this.localProps.store.selectedField?.isMultiple,
        )}
        parentStore={this.localProps.store}
        isPreviewMode={this.localProps.isPreviewMode}
        initialSortKey={defaults.sortKey ?? undefined}
        initialSortOrder={defaults.sortOrder}
        initialGroupByKey={defaults.groupByKey}
        initialFilterTagIds={defaults.filterTagIds}
        onClose={this.localProps.onClose}
        onBacklinksWorkflowClick={this.onBacklinksWorkflowClick}
      />
    )
  }

  private existingWorkflowsFilter = (workflow: Workflow) => {
    const { editablePermit, modalSearchValue } = this.localProps.store
    const searchValue = modalSearchValue || EMPTY_STRING
    const allowedTypes = this.allowedWorkflowTypes.map(type => type.type)

    const isSameWorkflow = editablePermit.id === workflow.id
    const includesName = workflow
      .getCaption(this.localProps.workflowTypesStore)
      .toLowerCase()
      .includes(searchValue.toLowerCase())
    // Resolved to the CURRENT type, so a workflow whose type has been deleted
    // drops out instead of riding along on the now-unrestricted field.
    const workflowType =
      this.localProps.workflowTypesStore?.getActualTypeByAnyVersionId(
        workflow.typeId,
      )
    const isAllowedType = isWorkflowTypeSelectable(workflowType, allowedTypes)

    return !isSameWorkflow && includesName && isAllowedType
  }

  /**
   * The field's configured type restriction, resolved against the live types.
   *
   * A restriction naming a type that has since been deleted resolves to nothing
   * and drops out here, so a restriction whose types are ALL deleted collapses
   * to an empty list — which every consumer reads as "no restriction". That
   * matches what the configurator already displays for such a field ("Any
   * workflow types"): before, the create-new path resolved through the store
   * while the existing-workflow predicate compared raw type strings, so the
   * same field offered every type to create and no workflow to pick.
   *
   * Deactivated types still resolve — only deletion removes a type from
   * `actualTypes` — so they stay restrictable and keep their inactive style.
   */
  private get allowedWorkflowTypes(): WorkflowType[] {
    return resolveAllowedWorkflowTypes(
      this.localProps.store.selectedField?.filterOptions,
      type => this.localProps.workflowTypesStore?.getActualTypeByType(type),
    )
  }

  private get allowCreateNew(): boolean {
    return this.localProps.store.selectedField?.allowCreateNew ?? true
  }

  private get allowSelectExisting(): boolean {
    return this.localProps.store.selectedField?.allowSelectExisting ?? true
  }

  private onSave = (selectedIds: string[]) => {
    this.localProps.store.changeSelectedFieldValue(selectedIds)
    this.localProps.store.updateSearchValue(EMPTY_STRING)
  }

  private onBacklinksWorkflowClick = (workflow: Workflow) => {
    this.localProps.store.openWorkflowHandler?.(workflow)
  }
}
